import { z } from 'zod';

import { SupportedLocales } from '@tdp/common/localization';
import { ILogger } from '@tdp/common/telemetry';

import { ITenantSettings } from './abstractions';
import { IUserLocaleReadResult } from './abstractions/ILocaleSettings';
import { ISettingsService, TenantSettingsFetchResult } from './abstractions/ISettingsService';
import { IUserAdminRoleReadResult } from './abstractions/IUserAdminRoleSettings';

export interface SettingsServiceDeps {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export class SettingsService implements ISettingsService {
  private logger: ILogger;
  private apiFetch: typeof fetch;

  constructor({ logger, apiFetch }: SettingsServiceDeps) {
    this.apiFetch = apiFetch;
    this.logger = logger;
  }

  async getTenantSettings(): Promise<TenantSettingsFetchResult> {
    const getTenantSettingsUrl = `/usersettings/v2/tenantSettings`;
    const response = await this.apiFetch(getTenantSettingsUrl);
    if (response.status != 200) {
      this.logger.error(
        'Network error when fetching tenant settings for user | reason: %s',
        response.statusText
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'networkError',
        errorMessage: response.statusText,
      });
    }
    // TODO: (astripa) Add parsing schema
    try {
      const dto = (await response.json()) as unknown as ITenantSettings;
      return {
        type: 'success',
        tenantSettings: dto,
      };
    } catch (error) {
      this.logger.error(
        'Encountered parsing errors on networ response payload'
      );
      return Promise.resolve({
        type: 'error',
        errorType: 'parsingError',
        errorMessage:
          error instanceof Error ? error.message : 'Unknown parsing error',
      });
    }
  }

  async readUserLocale(): Promise<IUserLocaleReadResult> {
    const response = await this.apiFetch('/userSettings/locale');

    if (response.status !== 200) {
      this.logger.error('Unexpected server status code: %d', response.status);
      return { type: 'failure', error: `Server responded with status code ${response.status.toString()}` };
    }

    const json = await response.json() as unknown;
    const parseResult = UserLocaleDTOSchema.safeParse(json);

    if (!parseResult.success) {
      this.logger.error("Invalid locale %s", parseResult.error);
      return { type: 'failure', error: "Invalid response format" };
    }

    // This is a bit of a hack to cast a 'lang' code into a locale, this is almost certainly going to annoy a user
    // somwhere because we're picking a dialect of a language that is not the one they want. (Looking at you 'es-es' 😑)
    const lang = parseResult.data.preferredLanguage.toLowerCase();
    const locale = SupportedLocales.find(locale => locale.includes(lang)) ?? 'en-us';
    return { type: 'success', locale };
  }

  async readUserAdminRole(): Promise<IUserAdminRoleReadResult> {
    const response = await this.apiFetch('/userSettings/isPrivilegedTeamsAdmin');

    if (response.status !== 200) {
      this.logger.error('Unexpected server status code: %d', response.status);
      return { type: 'failure', error: `Server responded with status code ${response.status.toString()}` };
    }

    const txt = await response.text();
    const isAdmin = Boolean(txt);

    return { type: 'success', isAdmin };
  }
}

const UserLocaleDTOSchema = z.object({
  preferredLanguage: z.string(),
});
